import { useNavigate } from "@tanstack/react-router"
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "design-system/molecules/Chart"
import { ChartCard } from "design-system/organisms/insights/ChartCard"
import { usePredefinedResponsesDistributionQuery } from "modules/insights/hooks/usePredefinedResponsesDistributionQuery"
import { getTextWidth } from "modules/shared/lib/charts"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { Bar, BarChart, LabelList, XAxis, YAxis } from "recharts"
import { TimeFrame } from "schema"
import { CustomChartLabel } from "../CustomChartLabel"

type Props = {
  timeFrame: TimeFrame
  campaignId?: string
  goalId: string
}

export function PredefinedResponsesDistribution({ timeFrame, campaignId, goalId }: Props) {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const { data, isPending } = usePredefinedResponsesDistributionQuery({
    timeFrame,
    campaignId,
    goalId,
  })

  const noData = !isPending && !data?.data.count

  const globalMax = useMemo(
    () => Math.max(...(data?.data.distribution.map((item) => item.count) ?? [])),
    [data],
  )

  const chartData = useMemo(() => {
    return data?.data.distribution
      .map((item) => {
        const currentValue = item.count
        return {
          key: item.optionValue,
          label: item.optionValue,
          goalOptionId: item.goalOptionId,
          value: currentValue,
        }
      })
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [data])

  const yAxisWidth = useMemo(() => {
    if (!chartData?.length) return 100
    const max = Math.max(...chartData.map((d) => getTextWidth(d.label)))
    return Math.min(Math.ceil(max), 135)
  }, [chartData])

  function handleBarClick(index: number) {
    const row = chartData?.[index]
    if (!row) return
    navigate({
      to: "/conversations/list",
      search: {
        filterBy: {
          campaignId,
          goalId,
          goalOptionId: row.goalOptionId,
        },
      },
    })
  }

  const chartConfig = {
    value: {
      label: t("insights.predefinedResponses.conversations"),
    },
  } satisfies ChartConfig

  const chart = (
    <ChartContainer config={chartConfig} className="mx-auto w-full h-full">
      <BarChart accessibilityLayer data={chartData} layout="vertical" margin={{ right: 40 }}>
        <YAxis
          dataKey="label"
          type="category"
          tickLine={false}
          tickMargin={8}
          axisLine={false}
          width={yAxisWidth}
          tick={(props) => (
            <CustomChartLabel
              {...props}
              x={Number(props.x)}
              y={Number(props.y)}
              maxWidth={yAxisWidth}
              dataLength={chartData?.length}
            />
          )}
        />
        <XAxis dataKey="value" type="number" hide domain={[0, globalMax]} />
        <ChartTooltip cursor={false} content={<ChartTooltipContent indicator="line" />} />
        <Bar
          dataKey="value"
          fill="var(--chart-secondary)"
          onClick={(_, index) => handleBarClick(index)}
          className="cursor-pointer"
          maxBarSize={40}
        >
          <LabelList
            dataKey="value"
            position="right"
            offset={8}
            className="fill-accent-foreground"
            fontSize={12}
          />
        </Bar>
      </BarChart>
    </ChartContainer>
  )

  return (
    <ChartCard
      title={t("insights.predefinedResponses.distribution.title")}
      description={t("insights.predefinedResponses.distribution.description")}
      chart={chart}
      isPending={isPending}
      noData={{
        show: noData,
        title: t("insights.predefinedResponses.noData.title"),
        description: t("insights.predefinedResponses.noData.description"),
      }}
    />
  )
}
